/**
 * Formatacao e leitura de esforco de tempo.
 *
 * O relogio corrente (h:mm:ss) e a leitura de historico (2h 15min) sao coisas
 * diferentes: uma serve para acompanhar o segundo passando, a outra para bater
 * o olho num total. Por isso duas funcoes, nao uma com flag.
 */

const doisDigitos = (valor) => String(valor).padStart(2, "0");

/** Relogio do cronometro: 00:42 ate uma hora, 1:05:09 depois. */
export const formatarCronometro = (segundos = 0) => {
  const total = Math.max(0, Math.floor(Number(segundos) || 0));
  const horas = Math.floor(total / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  const resto = total % 60;

  return horas > 0
    ? `${horas}:${doisDigitos(minutos)}:${doisDigitos(resto)}`
    : `${doisDigitos(minutos)}:${doisDigitos(resto)}`;
};

/** Leitura de total: "2h 15min", "45min", "—" quando nao houve trabalho. */
export const formatarDuracao = (segundos = 0, { vazio = "—" } = {}) => {
  const total = Math.max(0, Math.floor(Number(segundos) || 0));
  if (total === 0) return vazio;
  // Menos de um minuto ainda e trabalho: some se arredondar para "0min".
  if (total < 60) return `${total}s`;

  const horas = Math.floor(total / 3600);
  const minutos = Math.round((total % 3600) / 60);

  if (horas === 0) return `${minutos}min`;
  // 59min30s vira 60min: sobe a hora em vez de imprimir "2h 60min".
  if (minutos === 60) return `${horas + 1}h`;
  return minutos === 0 ? `${horas}h` : `${horas}h ${minutos}min`;
};

export const formatarMinutos = (minutos, opcoes) =>
  formatarDuracao((Number(minutos) || 0) * 60, opcoes);

/**
 * Le a duracao do jeito que a pessoa digita: "1h30", "1:30", "90", "1,5h".
 * Numero solto e minuto - quem aponta tempo pensa em minutos.
 */
export const parsearMinutos = (texto) => {
  const entrada = String(texto || "").trim().toLowerCase().replace(",", ".");
  if (!entrada) return null;

  const relogio = entrada.match(/^(\d+):([0-5]?\d)$/);
  if (relogio) return Number(relogio[1]) * 60 + Number(relogio[2]);

  const composto = entrada.match(/^(\d+(?:\.\d+)?)\s*h(?:oras?)?\s*(\d+)?\s*(?:min|m)?$/);
  if (composto) {
    const horas = Number(composto[1]);
    const minutos = composto[2] ? Number(composto[2]) : 0;
    return Math.round(horas * 60 + minutos);
  }

  const soMinutos = entrada.match(/^(\d+(?:\.\d+)?)\s*(?:min|m)?$/);
  if (soMinutos) return Math.round(Number(soMinutos[1]));

  return null;
};

/** Segundos ja decorridos de um segmento aberto, contados no relogio local. */
export const segundosDecorridos = (inicio) => {
  if (!inicio) return 0;
  const marco = new Date(inicio).getTime();
  if (Number.isNaN(marco)) return 0;
  return Math.max(0, Math.floor((Date.now() - marco) / 1000));
};

/**
 * Total exibido de uma tarefa: o acumulado no servidor mais o que o cronometro
 * desta sessao ja rodou. Sem a segunda parcela o numero da lista ficaria
 * congelado enquanto o relogio anda.
 */
export const segundosDaTarefa = (tarefa, emAndamento) => {
  const base = Number(tarefa?.tempo_segundos_total ?? tarefa?.tempo_segundos ?? 0);

  if (!emAndamento || Number(emAndamento.id_tarefa) !== Number(tarefa?.id)) return base;

  // O servidor ja contou o segmento aberto ate o instante da resposta.
  return base - Number(emAndamento.segundos || 0) + segundosDecorridos(emAndamento.inicio);
};

/** Compara esforco com estimativa: null quando nao ha promessa registrada. */
export const compararComEstimativa = (segundos, estimativaMinutos) => {
  const estimativa = Number(estimativaMinutos) || 0;
  if (estimativa <= 0) return null;

  const previstos = estimativa * 60;
  const percentual = Math.round((segundos / previstos) * 100);

  return {
    percentual,
    previstos,
    estourou: segundos > previstos,
    restanteSegundos: Math.max(0, previstos - segundos),
  };
};

export const formatarHorarioCurto = (valor) => {
  if (!valor) return "";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "";

  return data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

export const formatarDataCurta = (valor) => {
  if (!valor) return "";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "";

  const hoje = new Date();
  const mesmoDia = data.toDateString() === hoje.toDateString();
  if (mesmoDia) return "Hoje";

  const ontem = new Date(hoje.getTime() - 86400000);
  if (data.toDateString() === ontem.toDateString()) return "Ontem";

  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};
